/**
 * Fullstory Integration Configuration
 *
 * Centralizes all Fullstory SDK and decoration configuration for the marketing site.
 * Follows the universal-data-scoping-and-decoration and fullstory-getting-started skills.
 */

// Fullstory SDK configuration
export const FS_CONFIG = {
  // ORG_ID should match the Fullstory org configured in gatsby-ssr.js
  // Override here if needed for different environments
  orgId: process.env.GATSBY_FULLSTORY_ORG_ID || 'o-1RM0K1-na1', // Default to production org
  
  // Environment detection
  environment: process.env.NODE_ENV || 'development',
  isProduction: process.env.NODE_ENV === 'production',
  isStaging: process.env.GATSBY_BUILD_STAGE === 'true',
  isDevelopment: process.env.NODE_ENV === 'development',
  
  // Feature flags for gradual rollout
  features: {
    stableSelectors: true,
    pageProperties: true,
    elementProperties: true,
    analyticsEvents: true,
    componentWellbeing: true,
    cuaNavigation: true,
    userIdentification: true,
    diagnosticLogging: true,
    privacyControls: true,
  },
};

/**
 * Data Attribute Naming Convention
 * Following the Fullstory Data Attributes Guide convention
 */
export const DATA_ATTRIBUTES = {
  // Core stable selector attributes
  COMPONENT: 'data-component',
  ID: 'data-id',
  SECTION: 'data-section',
  POSITION: 'data-position',
  STATE: 'data-state',
  
  // Fullstory-specific properties
  PROPERTIES_SCHEMA: 'data-fs-properties-schema',
  ELEMENT: 'data-fs-element',
  CLASSIFICATION: 'data-fs-classification',
  
  // Development/debugging (strip in production)
  SOURCE_FILE: 'data-source-file',
  SOURCE_LINE: 'data-source-line',
  
  // Test automation
  TESTID: 'data-testid',
  AUTOMATION_PATH: 'data-automation-path',
  
  // CUA navigation hints
  INTERACTIVE: 'data-interactive',
  ROLE_HINT: 'data-role-hint',
  PURPOSE: 'data-purpose',
} as const;

/**
 * Component Type Taxonomy
 * Use lowercase kebab-case for consistency with Fullstory Data Attributes Guide
 */
export const COMPONENT_TYPES = {
  PAGE: 'page',
  BUTTON: 'button',
  LINK: 'link',
  CARD: 'card',
  FORM: 'form',
  INPUT: 'input',
  CHECKBOX: 'checkbox',
  RADIO: 'radio',
  SELECT: 'select',
  TEXTAREA: 'textarea',
  MODAL: 'modal',
  DROPDOWN: 'dropdown',
  MENU: 'menu',
  NAVIGATION: 'navigation',
  HEADER: 'header',
  FOOTER: 'footer',
  HERO: 'hero',
  SECTION: 'section',
  NOTIFICATION_BAR: 'notification-bar',
  ACCORDION: 'accordion',
  TABS: 'tabs',
  CAROUSEL: 'carousel',
  TABLE: 'table',
  LISTITEM: 'list-item',
  BADGE: 'badge',
  TOOLTIP: 'tooltip',
  ALERT: 'alert',
  BREADCRUMB: 'breadcrumb',
  PAGINATION: 'pagination',
  SEARCH: 'search',
  FILTER: 'filter',
  TOGGLE: 'toggle',
  VIDEO: 'video',
  // Themed feature areas — these are bigger than primitives so they get
  // their own component types so analytics/CUA can filter the whole feature.
  GAME: 'game',
  QUIZ: 'quiz',
  READINESS_SCORE: 'readiness-score',
  BENCHMARK_REPORT: 'benchmark-report',
} as const;

/**
 * Page Section Taxonomy
 * Major sections of the marketing site
 */
export const PAGE_SECTIONS = {
  HEADER: 'header',
  HERO: 'hero',
  NAVIGATION: 'navigation',
  MAIN_CONTENT: 'main-content',
  SIDEBAR: 'sidebar',
  FEATURED: 'featured',
  BENEFITS: 'benefits',
  FEATURES: 'features',
  TESTIMONIALS: 'testimonials',
  PRICING: 'pricing',
  CASE_STUDIES: 'case-studies',
  FAQ: 'faq',
  CTA: 'call-to-action',
  FOOTER: 'footer',
  SOCIAL: 'social',
  FORM_SECTION: 'form-section',
} as const;

/**
 * Component State Values
 * Captures semantic state for component wellbeing tracking
 */
export const COMPONENT_STATES = {
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
  DISABLED: 'disabled',
  ENABLED: 'enabled',
  EXPANDED: 'expanded',
  COLLAPSED: 'collapsed',
  FOCUSED: 'focused',
  BLURRED: 'blurred',
  CHECKED: 'checked',
  UNCHECKED: 'unchecked',
  HIDDEN: 'hidden',
  VISIBLE: 'visible',
  STALE: 'stale',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SELECTED: 'selected',
  UNSELECTED: 'unselected',
} as const;

/**
 * Property Types for Element Properties Schema
 * Defines JSON schema types for data-fs-properties-schema
 */
export const PROPERTY_TYPES = {
  STRING: 'str',
  INTEGER: 'int',
  REAL: 'real',
  BOOLEAN: 'bool',
  DATE: 'date',
  ARRAY: 'array',
} as const;

/**
 * Classification Levels for Privacy Controls
 * Used with data-fs-classification attribute
 */
export const CLASSIFICATION_LEVELS = {
  PII: 'pii',
  FINANCIAL: 'financial',
  RESTRICTED: 'restricted',
  UNRESTRICTED: 'unrestricted',
} as const;

/**
 * Role Hints for CUA Navigation
 * Helps AI agents understand element purpose
 */
export const ROLE_HINTS = {
  PRIMARY_ACTION: 'primary-action',
  SECONDARY_ACTION: 'secondary-action',
  SUBMIT: 'submit',
  CANCEL: 'cancel',
  CLOSE: 'close',
  NAVIGATE: 'navigate',
  FILTER: 'filter',
  SEARCH: 'search',
  SORT: 'sort',
  EXPAND: 'expand',
  COLLAPSE: 'collapse',
  OPEN_MODAL: 'open-modal',
  OPEN_MENU: 'open-menu',
  COPY: 'copy',
} as const;

/**
 * Event Categories for Analytics
 * Used with FS('event') API
 */
export const EVENT_CATEGORIES = {
  ENGAGEMENT: 'engagement',
  CONVERSION: 'conversion',
  ERROR: 'error',
  PERFORMANCE: 'performance',
  NAVIGATION: 'navigation',
  FORM: 'form',
  SEARCH: 'search',
  SOCIAL: 'social',
  AUTHENTICATION: 'authentication',
  PAGE: 'page',
} as const;

/**
 * Privacy Consent States
 * For fullstory-user-consent skill implementation
 */
export const CONSENT_STATES = {
  PENDING: 'pending',
  GRANTED: 'granted',
  DENIED: 'denied',
  REVOKED: 'revoked',
} as const;
